import type { WallBoard, WallCard } from '@walls/store';
import { getClusterDropTargetFromRect } from '../../positioning/clusterDrop';
import type { ClusterChildSortDrag } from '../types';

export function getClusterChildDragSizes(
  rect: Pick<DOMRect, 'height' | 'width'>,
  zoom: number
) {
  const normalizedZoom = Number.isFinite(zoom) && zoom > 0 ? zoom : 1;

  return {
    boardSize: {
      height: rect.height / normalizedZoom,
      width: rect.width / normalizedZoom,
    },
    screenSize: {
      height: rect.height,
      width: rect.width,
    },
  };
}

export function isPointInElement(clientX: number, clientY: number, element: Element | null) {
  if (!element) {
    return false;
  }

  const rect = element.getBoundingClientRect();
  return (
    clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom
  );
}

export function getDetachedCardPosition({
  clientX,
  clientY,
  drag,
  viewport,
  zoom,
}: {
  clientX: number;
  clientY: number;
  drag: ClusterChildSortDrag;
  viewport: HTMLElement | null;
  zoom: number;
}) {
  if (!viewport) {
    return {
      xPos: Math.round(clientX - drag.grabOffsetX),
      yPos: Math.round(clientY - drag.grabOffsetY),
    };
  }

  const viewportRect = viewport.getBoundingClientRect();

  return {
    xPos: Math.round(viewport.scrollLeft + (clientX - drag.grabOffsetX - viewportRect.left) / zoom),
    yPos: Math.round(viewport.scrollTop + (clientY - drag.grabOffsetY - viewportRect.top) / zoom),
  };
}

export function getClusterChildDragRect(drag: ClusterChildSortDrag) {
  return {
    height: drag.screenSize.height,
    width: drag.screenSize.width,
    x: drag.pointerX - drag.grabOffsetX,
    y: drag.pointerY - drag.grabOffsetY,
  };
}

export function getClusterDropTargetFromChildDrag({
  board,
  card,
  cards,
  currentParticipantId,
  drag,
  viewport,
  zoom,
}: {
  board: WallBoard;
  card: WallCard;
  cards: WallCard[];
  currentParticipantId: string | null;
  drag: ClusterChildSortDrag;
  viewport: HTMLElement | null;
  zoom: number;
}) {
  const draggedRect = getClusterChildDragRect(drag);

  if (!viewport) {
    return getClusterDropTargetFromRect({
      board,
      card,
      cards,
      currentParticipantId,
      draggedRect,
      pointerPosition: {
        x: drag.pointerX,
        y: drag.pointerY,
      },
    });
  }

  const viewportRect = viewport.getBoundingClientRect();

  return getClusterDropTargetFromRect({
    board,
    card,
    cards,
    currentParticipantId,
    draggedRect: {
      height: drag.boardSize.height,
      width: drag.boardSize.width,
      x: viewport.scrollLeft + (draggedRect.x - viewportRect.left) / zoom,
      y: viewport.scrollTop + (draggedRect.y - viewportRect.top) / zoom,
    },
    pointerPosition: {
      x: viewport.scrollLeft + (drag.pointerX - viewportRect.left) / zoom,
      y: viewport.scrollTop + (drag.pointerY - viewportRect.top) / zoom,
    },
  });
}

export function getDraggedRectTargetChildId(
  draggedRect: {
    height: number;
    width: number;
    x: number;
    y: number;
  },
  parentElement: Element | null,
  movedChildId: string,
  isHorizontal: boolean
) {
  if (!parentElement) {
    return null;
  }

  const probeX = draggedRect.x + draggedRect.width / 2;
  const probeY = draggedRect.y + draggedRect.height / 2;
  const childElements = Array.from(
    parentElement.querySelectorAll<HTMLElement>('[data-cluster-child-id]')
  );
  let lastPreviousIndex = -1;

  for (let childIndex = 0; childIndex < childElements.length; childIndex += 1) {
    const childElement = childElements[childIndex];
    const childId = childElement.dataset['clusterChildId'];

    if (!childId || childId === movedChildId) {
      continue;
    }

    const rect = childElement.getBoundingClientRect();

    if (isHorizontal) {
      if (probeY > rect.top && probeY < rect.bottom && probeX < rect.left + rect.width / 2) {
        return childId;
      }

      if (probeY > rect.top && probeY < rect.bottom && probeX < rect.right) {
        return childElements[childIndex + 1]?.dataset['clusterChildId'] ?? null;
      }

      if (probeY > rect.top && probeY < rect.bottom && probeX > rect.right) {
        lastPreviousIndex = childIndex;
      }

      continue;
    }

    if (probeX > rect.left && probeX < rect.right && probeY < rect.top + rect.height / 2) {
      return childId;
    }

    if (probeX > rect.left && probeX < rect.right && probeY < rect.bottom) {
      return childElements[childIndex + 1]?.dataset['clusterChildId'] ?? null;
    }

    if (probeX > rect.left && probeX < rect.right && probeY > rect.bottom) {
      lastPreviousIndex = childIndex;
    }
  }

  return lastPreviousIndex >= 0
    ? childElements[lastPreviousIndex + 1]?.dataset['clusterChildId'] ?? null
    : childElements[0]?.dataset['clusterChildId'] ?? null;
}
